import React, { useEffect, useRef } from 'react';

import LabeledCheckbox from './LabeledCheckbox';

import { logValidationError, FormType } from 'businessLogic/utils/validationLogs';
import InputErrorMessage from 'components/Shared/Inputs/InputErrorMessage';
import LabeledInput from 'components/Shared/Inputs/LabeledInput';

export interface Props {
  label: string | React.ReactNode;
  className?: string;
  validationError?: number | boolean;
  translator: Function;
  validationValues?: {};
  setFieldErrorMessage?: (errorComponent: any) => any;
  [propName: string]: any;
  checked?: boolean;
  type?: 'checkbox' | 'text';
  /** Optional: Name of the field for logging purposes */
  name?: string;
  /** Optional: Form type for validation error logging (e.g., 'creditCard', 'bankAccount') */
  formType?: FormType;
  /** Optional: Whether to skip logging validation errors (useful for testing) */
  skipValidationLogging?: boolean;
}
const ValidatedLabeledInput: React.FC<Props> = (props) => {
  const {
    validationError,
    translator,
    validationValues = {},
    setFieldErrorMessage,
    type,
    checked = false,
    name,
    formType = 'other',
    skipValidationLogging = false,
    ...rest
  } = props;

  const isInError = typeof validationError === 'number';

  // Compute field name once to avoid dependency on rest object
  // Prefer explicit name prop, fallback to rest.name for backward compatibility, then 'unknown'
  const fieldName = name || rest.name || 'unknown';

  // Track the last logged error to prevent duplicate logging
  const lastLoggedErrorRef = useRef<number | undefined>(undefined);

  // Log validation error when it is first displayed to the user
  // IMPORTANT: This only logs when an error FIRST appears or when the error TYPE changes.
  // This prevents log spam - we don't want to log on every re-render while the error is still showing.
  // For example, if a user types in an invalid card number, we log once when the error appears,
  // but not on every subsequent keystroke while the error remains visible.
  useEffect(() => {
    if (isInError && validationError !== lastLoggedErrorRef.current && !skipValidationLogging) {
      logValidationError({
        fieldName,
        errorType: validationError as number,
        formType,
        logger: 'components/Shared/Inputs/ValidatedLabeledInput',
      });
      lastLoggedErrorRef.current = validationError as number;
    }

    // Clear the ref when error is cleared (so we log again if same error reappears)
    if (!isInError) {
      lastLoggedErrorRef.current = undefined;
    }
  }, [isInError, validationError, fieldName, formType, skipValidationLogging]);

  // @ts-ignore
  const getErrorComponent = () => {
    const errorComponent = (isInError && (
      <InputErrorMessage id={translator(validationError)} values={validationValues} />
    )) || <></>;

    if (setFieldErrorMessage) {
      // Passing error component to parent and not displaying it in ValidatedLabeledInput component
      setFieldErrorMessage(errorComponent);
    } else {
      // Displaying the error component in ValidatedLabeledInput component
      return errorComponent;
    }
  };

  return (
    <div>
      {type === 'checkbox' ? (
        //@ts-ignore
        <LabeledCheckbox {...rest} checked={checked} isInError={isInError} />
      ) : (
        <>
          <LabeledInput {...rest} isInError={isInError} />
          {getErrorComponent()}
        </>
      )}
      <style jsx>{`
        .field-error-message-wrapper {
          line-height: 14px;

          .error-icon-wrapper {
            position: absolute;
          }

          .error-message {
            display: block;
            padding-left: 12px;
            font-family: AvenirNextforINTUIT-Medium;
          }
        }
      `}</style>
    </div>
  );
};

export default ValidatedLabeledInput;
